import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { isMockActive } from '../utils/isMockActive';
import { vydejWsTicket } from '../api/JsdhNotifikaceService/service/ChatService';
import { pripojChat, type ChatSpojeni, type StavChatSpojeni } from '../services/chat/chatSocket';

interface UseChatSocketVysledek {
    stav: StavChatSpojeni;
    /** Jmena tech, kdo prave pisi (bez nas). Prazdne pole = nikdo. */
    pisici: string[];
    /** Zavolat pri psani. Skrceni resi spojeni, da se volat pri kazde klavese. */
    oznamPsani: () => void;
}

/**
 * HO-152: React vrstva nad `chatSocket`. Drzi spojeni po dobu zivota stranky
 * chatu a preklada serverove `ZMENA` na zneplatneni React Query cache.
 *
 * Vraceny `stav` neni jen informace pro uzivatele - stranka podle nej ridi
 * dotazovani: kdyz socket zije, dotazovani se zpomali na zachranou sit, kdyz
 * spadne, vrati se na puvodni rychle kolo. Chat proto funguje i za proxy, ktera
 * `Upgrade` nepousti.
 */
export const useChatSocket = (konverzaceId: number | undefined, povoleno = true): UseChatSocketVysledek => {
    const { refreshToken } = useAuth();
    const queryClient = useQueryClient();
    const [stav, setStav] = useState<StavChatSpojeni>('PRIPOJUJI');
    const [pisici, setPisici] = useState<string[]>([]);
    const spojeniRef = useRef<ChatSpojeni | null>(null);

    // Refresh token se ROTUJE. Kdyby byl v zavislostech efektu, kazde obnoveni
    // prihlaseni by spojeni zbytecne shodilo a postavilo znovu; takhle si ho
    // vyzvedne az ticket, ktery ho opravdu potrebuje, a vzdy ten aktualni.
    const tokenRef = useRef(refreshToken);
    tokenRef.current = refreshToken;

    useEffect(() => {
        if (!konverzaceId || !povoleno || isMockActive()) {
            return;
        }
        const spojeni = pripojChat({
            konverzaceId,
            ziskejTicket: async () => {
                const token = tokenRef.current;
                if (!token) {
                    // Relace z rychleho prihlaseni na webu neexistuje - bez refresh
                    // tokenu by server vratil 400 a klient by na nej marne dotiral.
                    throw new Error('Relace nema refresh token');
                }
                const odpoved = await vydejWsTicket(token);
                return odpoved.ticket;
            },
            naZmenu: () => {
                queryClient.invalidateQueries({ queryKey: ['chat', 'zpravy', konverzaceId] });
                queryClient.invalidateQueries({ queryKey: ['chat', 'kanalJednotky'] });
            },
            naPise: setPisici,
            naStav: setStav,
        });
        spojeniRef.current = spojeni;
        return () => {
            spojeniRef.current = null;
            spojeni.zavri();
            setPisici([]);
        };
    }, [konverzaceId, povoleno, queryClient]);

    const oznamPsani = useCallback(() => {
        spojeniRef.current?.oznamPsani();
    }, []);

    return { stav, pisici, oznamPsani };
};
